/**
 * Build a client-side route under the active workspace.
 *
 *   const wp = useWorkspacePath()
 *   <Link to={wp('/results')}>Runs</Link>
 *   navigate(wp('/applications'))
 *
 * Single helper used by every absolute-path navigation that needs
 * workspace context. Reads from useAppContextOptional so it can be
 * called from layout shells (Sidebar, Header) that render in BOTH
 * workspace-scoped and identity-only (admin) modes without crashing.
 * When called outside a workspace context, returns a function that
 * produces `/__no-workspace__/<path>` — never actually rendered
 * because workspace-scoped UI gates on the same context's presence
 * upstream and skips emitting these links. The sentinel makes a
 * regression (a leaking workspace-scoped link in admin mode) loud
 * instead of silent.
 *
 * For navigating between sibling pages of the same workspace, this is
 * the canonical idiom. Within a single workspace section, react-router
 * relative paths (`<Link to="../results">`) also work; this helper is
 * for absolute jumps where relative paths are awkward.
 */
import { useAppContextOptional } from './useAppContext'

export function useWorkspacePath(): (path: string) => string {
  const ctx = useAppContextOptional()
  const workspaceId = ctx?.workspaceId ?? '__no-workspace__'
  return (path: string) => {
    const normalized = path.startsWith('/') ? path : `/${path}`
    return `/workspaces/${workspaceId}${normalized}`
  }
}
